import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Card, CardContent, TextField,
    Button, Box, Divider, Typography, Alert,
    Link as MuiLink,
} from "@mui/material";
import { Link } from "react-router-dom";
import api from "../../shared/api";

const schema = z.object({
    code: z.string().min(1, "The code field is required."),
});

export default function MultiFactorAuthForm({ viewModel }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");

    const { control, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { code: "" },
    });

    const onVerify = async (data) => {
        setFormError("");
        setLoading(true);
        try {
            const response = await api.put("/multi-factor-authentication", data, { responseType: "json" });
            if (response.data.location) {
                window.location.replace(response.data.location);
                return;
            }
            if (!response.data.valid && response.data.errors) {
                let errorMsg = "";
                response.data.errors.forEach((err) => {
                    if (err.PROPERTY.length === 0) {
                        errorMsg += " " + err.MESSAGE;
                    }
                });
                setFormError(errorMsg);
                setStep(3);
            }
        } catch {
            setFormError("There was a network error processing this request. Please contact support if this problem persists.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card id="multifactor" variant="elevation" elevation={0} square sx={{ border: "none" }}>
            <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5">Multi-Factor Authentication</Typography>
            </CardContent>

            {step === 1 && (
                <form onSubmit={handleSubmit(onVerify)}>
                    <CardContent>
                        <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Please enter the code you received in your email.
                            </Typography>

                            <Controller
                                name="code"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Code"
                                        autoFocus
                                        fullWidth
                                        error={!!errors.code}
                                        helperText={errors.code?.message}
                                        sx={{ mb: 3 }}
                                        onKeyDown={(e) => e.key === "Enter" && handleSubmit(onVerify)()}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            {formError ? (
                                <Typography variant="subtitle1" color="error">{formError}</Typography>
                            ) : (
                                <Typography variant="subtitle1">&nbsp;</Typography>
                            )}
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, py: 1.2 }}
                            >
                                {loading ? "Validating..." : "Validate Code"}
                            </Button>
                        </Box>
                    </CardContent>

                    <Divider />
                    <CardContent>
                        <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                            <MuiLink component={Link} to="/login" display="block">Resend Code</MuiLink>
                        </Box>
                    </CardContent>
                </form>
            )}

            {step === 3 && (
                <CardContent>
                    <Alert severity="error" variant="outlined">
                        <span dangerouslySetInnerHTML={{ __html: formError }} />
                        {" "}You must start a new{" "}
                        <MuiLink component={Link} to="/login">multi-factor request</MuiLink>.
                        {" "}All verification codes previously sent to you have been invalidated.
                    </Alert>
                </CardContent>
            )}
        </Card>
    );
}
